import React from 'react';
import RatesSection from './scorecard/RatesSection';
import ReliabilitySection from './scorecard/ReliabilitySection';
import InfrastructureSection from './scorecard/InfrastructureSection';
import EnvironmentalSection from './scorecard/EnvironmentalSection';

function UtilityScorecard({
  utilityId,
  utilityData,
  nationalAverages,
  selectedYear,
  onClose
}) {
  if (!utilityData) {
    return (
      <div className="p-6 text-center text-gray-500">
        <p>No data available for this utility</p>
      </div>
    );
  }

  const yearData = utilityData.years?.[selectedYear];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">
              {utilityData.display_name || utilityData.name || `Utility ${utilityId}`}
            </h2>
            {/* Show legal name and parent company if different from display name */}
            {(utilityData.parent_company || (utilityData.display_name && utilityData.display_name !== utilityData.name)) && (
              <p className="text-sm text-gray-600 truncate">
                {utilityData.name}
                {utilityData.parent_company && ` · ${utilityData.parent_company}`}
              </p>
            )}
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-sm text-gray-500">
                {utilityData.states?.join(', ') || 'Unknown'}
              </span>
              <span className="text-gray-300">|</span>
              <span className="text-sm font-medium text-blue-600">
                {selectedYear}
              </span>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Customer count if available */}
        {yearData?.rates?.total_customers && (
          <div className="mt-2 text-sm text-gray-600">
            <span className="font-medium">{yearData.rates.total_customers.toLocaleString()}</span> total customers
          </div>
        )}
      </div>

      {/* Sections - scrollable */}
      <div className="flex-1 overflow-auto scorecard-scroll">
        {!yearData ? (
          <div className="p-6 text-center text-gray-500">
            <p>No data available for {selectedYear}</p>
            <p className="text-sm mt-2">Try selecting a different year</p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Section 1: Rates & Affordability */}
            <RatesSection
              data={yearData.rates}
              nationalAvg={nationalAverages?.rates}
              utilityData={utilityData}
              selectedYear={selectedYear}
            />

            {/* Section 2: Reliability */}
            <ReliabilitySection
              data={yearData.reliability}
              nationalAvg={nationalAverages?.reliability}
              utilityData={utilityData}
              selectedYear={selectedYear}
            />

            {/* Section 3: Infrastructure & Innovation */}
            <InfrastructureSection
              data={yearData.infrastructure}
              nationalAvg={nationalAverages?.infrastructure}
              utilityData={utilityData}
              selectedYear={selectedYear}
            />

            {/* Section 4: Environmental Performance */}
            <EnvironmentalSection
              data={yearData.environmental}
              nationalAvg={nationalAverages?.environmental}
              utilityData={utilityData}
              selectedYear={selectedYear}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default UtilityScorecard;
